import { EventType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../../api/generated';
import { loginRequestPrompt, msalConfig } from '../authConfig';
import { IAzureAuthConfig } from '../authModels';

export let logout: () => Promise<void>;
export let login: () => Promise<void>;

export const AzureAuthentificator: FC<{
    saveRoles: (roles: string[], username: string) => void;
}> = ({ saveRoles }) => {
    const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | undefined>();
    const [scopes, setScopes] = useState<string[]>([]);

    useEffect(() => {
        (async () => {
            const response: IAzureAuthConfig = await (await fetch('/api/config/auth-settings/azure')).json();
            setScopes([response.scope]);

            try {
                if (response && response.clientId && response.tenantId) {
                    setMsalInstance(
                        new PublicClientApplication({
                            ...msalConfig,
                            auth: {
                                clientId: response.clientId,
                                authority: 'https://login.microsoftonline.com/' + response.tenantId
                            }
                        })
                    );                  
                } else {
                    console.error('ClientId or TenantId is undefined');
                }
            } catch (error) {
                console.error('Error initializing MSAL:', error);
            }
        })();
    }, []);

    if (!msalInstance) {
        return null;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <GetInfo saveRoles={saveRoles} scopes={scopes} />
        </MsalProvider>
    );
};

const GetInfo: FC<{ saveRoles: (roles: string[], username: string) => void; scopes: string[] }> = ({ saveRoles, scopes }) => {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            window['getToken'] = async () => {
                const response = await instance.acquireTokenSilent({
                    account: accounts[0],
                    scopes
                });

                return response.accessToken;
            };
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            const account = accounts[0];
            const roles = account.idTokenClaims?.['roles'];
            if (!roles || roles.length == 0) {
                navigate('/logout');
                return;
            }
            saveRoles(account.idTokenClaims?.['roles'] as string[], account.username);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (inProgress === 'none' && !isAuthenticated) {
            instance.loginRedirect({ ...loginRequestPrompt, scopes }).catch((e) => {
                console.log(e);
            });
        }
    }, [inProgress, isAuthenticated]);

    useEffect(() => {
        const callbackId = instance.addEventCallback((message) => {
            if (message.eventType === EventType.LOGIN_SUCCESS) {
                window['getToken'] = async () => {
                    const result = message.payload;
                    const response = await instance.acquireTokenSilent({
                        account: result.account,
                        scopes
                    });

                    return response.accessToken;
                };

                (async () => {
                    var username = instance.getAllAccounts()[0].username;
                    await api.user.logLoginTime({ username, userState: 1 });
                })();
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, []);

    useEffect(() => {
        logout = async () => {
            var username = instance.getAllAccounts()[0].username;
            await api.user.logLoginTime({ username, userState: 2 });
            await instance.logoutRedirect();
        };

        login = async () => {
            await instance.loginRedirect({ ...loginRequestPrompt, scopes });
        };
    }, []);

    return null;
};
